import styled from '@emotion/styled'
import { LogoWrapper } from './styles'

import DLetter       from '@/assets/icons/DLetter.svg'
import ELetter       from '@/assets/icons/ELetter.svg'
import ILetter       from '@/assets/icons/ILetter.svg'
import TLetter       from '@/assets/icons/TLetter.svg'
import RLetter       from '@/assets/icons/RLetter.svg'
import ECustomLetter from '@/assets/icons/ECustomLetter.svg'
import SLetter       from '@/assets/icons/SLetter.svg'

/**
 * LogoItem — Absolutely positioned letter with expand/collapse animation.
 *
 * When the sidebar collapses, every letter moves to `collapsedPosition`
 * (the ECustom centered position) and fades out, except ECustom which
 * stays at collapsedPosition visible — acting as the collapsed icon.
 *
 * When the sidebar expands, each letter translates to `expandedPosition`
 * (its correct place within the word) and fades in.
 */
const LogoItem = styled('img')<{
  expanded: boolean
  expandedPosition: number
  collapsedPosition: number
  isAnchor?: boolean
  isColored?: boolean
  timeTotransition: number
}>(({ expanded, expandedPosition, collapsedPosition, isAnchor, isColored, timeTotransition }) => ({
  opacity:  isAnchor || expanded ? 1 : 0,
  position: 'absolute',
  height:   20,
  width:    'auto',
  top:      '50%',
  left:     22,
  transform: `translateX(${expanded ? expandedPosition : collapsedPosition}px) translateY(-50%)`,
  transition: [
    `opacity 0.${timeTotransition - 4}s ease`,
    `transform 0.${timeTotransition}s ease`,
  ].join(', '),
  filter:       isColored ? 'none' : 'brightness(0) invert(1)',
  userSelect:   'none',
  pointerEvents:'none',
}))

/*
 * Letter layout at height: 20px (2× scale from 10px SVG viewBox height).
 *
 * Widths at 2× scale (rounded):
 *   D=18  E=14  I=4  T=16  R=16  ECustom=14  S=15
 *   Gap: 7px
 *
 * expandedPosition (translateX from left: 22):
 *   D=0  E=25  I=46  T=57  R=80  ECustom=103  S=124
 *
 * collapsedPosition for all: 1
 *   → ECustom at left:22 + translateX:1 = 23px ≈ center of 60px sidebar
 *   → All other letters collapse TO that same position and fade out
 */
const COLLAPSED = 1

type AppLogoProps = {
  expanded: boolean
  onClick: () => void
}

export default function AppLogo({ expanded, onClick }: Readonly<AppLogoProps>) {
  return (
    <LogoWrapper onClick={onClick}>
      {/* D */}
      <LogoItem
        src={DLetter}       alt=""
        expanded={expanded}
        expandedPosition={0}
        collapsedPosition={COLLAPSED}
        timeTotransition={10}
      />
      {/* E */}
      <LogoItem
        src={ELetter}       alt=""
        expanded={expanded}
        expandedPosition={25}
        collapsedPosition={COLLAPSED}
        timeTotransition={9}
      />
      {/* I */}
      <LogoItem
        src={ILetter}       alt=""
        expanded={expanded}
        expandedPosition={46}
        collapsedPosition={COLLAPSED}
        timeTotransition={8}
      />
      {/* T */}
      <LogoItem
        src={TLetter}       alt=""
        expanded={expanded}
        expandedPosition={57}
        collapsedPosition={COLLAPSED}
        timeTotransition={7}
      />
      {/* R */}
      <LogoItem
        src={RLetter}       alt=""
        expanded={expanded}
        expandedPosition={80}
        collapsedPosition={COLLAPSED}
        timeTotransition={6}
      />
      {/* Ẽ custom — ANCHOR: stays visible when collapsed, centered in 60px sidebar */}
      <LogoItem
        src={ECustomLetter} alt=""
        expanded={expanded}
        isAnchor
        isColored
        expandedPosition={103}
        collapsedPosition={COLLAPSED}
        timeTotransition={5}
      />
      {/* S */}
      <LogoItem
        src={SLetter}       alt=""
        expanded={expanded}
        expandedPosition={124}
        collapsedPosition={COLLAPSED}
        timeTotransition={6}
      />
    </LogoWrapper>
  )
}
